import React from 'react';
import {
    Button,
    Dialog,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    DialogOpenChangeData,
    DialogTrigger,
    Text,
    makeStyles,
    mergeClasses,
    shorthands,
} from '@fluentui/react-components';
import { Dismiss24Regular } from '@fluentui/react-icons';
import { ReactComponent as PersonaIcon } from '../../../../../assets/toolkit/persona.svg';
import { customColors, customFonts } from '../../../../../styles';
import type { UsePersonaVm } from '../../../hooks/usePersona';
import PersonaEditorView from './PersonaEditorView';
import PersonaLibraryView from './PersonaLibraryView';

const trayButtonIcon = '& .toolkitTrayIcon';
const trayButtonIconSvg = '& .toolkitTrayIcon svg';

const useStyles = makeStyles({
    trayButton: {
        height: '32px',
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
        ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),
        ...shorthands.borderRadius('5px'),
        ...shorthands.padding('0px', '12px'),
        backgroundColor: customColors.white,
        whiteSpace: 'nowrap',
        transition: 'all 0.2s ease',

        [trayButtonIconSvg]: {
            color: customColors.protivitiDarkBlue,
            transition: 'all 0.2s ease',
        },

        '&:hover': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            ...shorthands.border('1px', 'solid', customColors.protivitiDarkBlue),

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },

        '&:active': {
            backgroundColor: customColors.protivitiDarkBlue,
            color: customColors.white,
            transition: 'all 0s',

            [trayButtonIcon]: {
                backgroundColor: 'transparent',
            },

            [trayButtonIconSvg]: {
                color: customColors.white,
            },
        },
    },
    personaDialog: {
        width: '560px',
        ...shorthands.padding('0px'),
    },
    personaTitleRow: {
        display: 'grid',
        gridTemplateColumns: '32px 1fr 32px',
        alignItems: 'start',
        width: '100%',
        boxSizing: 'border-box',
        ...shorthands.padding('20px', '20px', '8px', '20px'),
    },
    personaTitleSpacer: {
        width: '32px',
        height: '32px',
    },
    personaTitleBlock: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        textAlign: 'center',
        gap: '4px',
        width: '100%',
        boxSizing: 'border-box',
        ...shorthands.padding('0px', '32px'),
    },
    personaTitle: {
        fontFamily: customFonts.Lato,
        fontSize: '22px',
        fontWeight: '700',
        color: customColors.protivitiDarkBlue,
    },
    personaSubtitle: {
        fontFamily: customFonts.Lato,
        fontSize: '14px',
        color: '#5C6B7A',
    },
    closeDialogButton: {
        minWidth: '28px',
        height: '28px',
        justifySelf: 'end',
    },
    personaContent: {
        display: 'flex',
        flexDirection: 'column',
        gap: '12px',
        ...shorthands.padding('8px', '20px', '20px', '20px'),
    },
    deletePersonaDialog: {
        width: '450px',
        borderRadius: '15px',
    },
    deletePersonaActions: {
        paddingTop: '10%',
    },
    deletePersonaDelete: {
        backgroundColor: customColors.protivitiOrange,
        '&:hover': {
            backgroundColor: customColors.summarizeButtonDarker,
            color: customColors.white,
        },
    },
    deletePersonaClose: {
        backgroundColor: customColors.white,
        '&:hover': {
            backgroundColor: customColors.summarizeButtonDarker,
            color: customColors.white,
        },
    },
    deletePersonaButton: {
        fontFamily: customFonts.Lato,
        fontSize: '12px',
        borderRadius: '5px',
        ...shorthands.border('1.5px', 'solid', customColors.protivitiOrange),
    },
    deletePersonaTitle: {
        fontFamily: customFonts.TitleFont,
        fontWeight: '500',
        fontSize: '24px',
    },
});

interface PersonaTrayItemProps {
    vm: UsePersonaVm;
}

const PersonaTrayItem: React.FC<PersonaTrayItemProps> = ({ vm }) => {
    const styles = useStyles();

    return (
        <>
            <Button
                className={styles.trayButton}
                appearance="transparent"
                icon={<PersonaIcon className="toolkitTrayIcon" />}
                onClick={vm.openPersonaDialog}
            >
                Persona
            </Button>

            <Dialog
                open={vm.isPersonaDialogOpen}
                onOpenChange={(_event, data: DialogOpenChangeData) => {
                    if (!data.open) {
                        vm.closePersonaDialog();
                    }
                }}
            >
                <DialogSurface className={styles.personaDialog}>
                    <DialogBody>
                        <DialogTitle>
                            <div className={styles.personaTitleRow}>
                                <div className={styles.personaTitleSpacer} />
                                <div className={styles.personaTitleBlock}>
                                    <Text className={styles.personaTitle}>Persona</Text>
                                    <Text className={styles.personaSubtitle}>
                                        The persona defines the agent's response style.
                                    </Text>
                                </div>
                                <Button
                                    className={styles.closeDialogButton}
                                    appearance="subtle"
                                    icon={<Dismiss24Regular />}
                                    aria-label="Close persona dialog"
                                    onClick={vm.closePersonaDialog}
                                />
                            </div>
                        </DialogTitle>
                        <DialogContent className={styles.personaContent}>
                            {vm.viewMode === 'library' ? <PersonaLibraryView vm={vm} /> : <PersonaEditorView vm={vm} />}
                        </DialogContent>
                    </DialogBody>
                </DialogSurface>
            </Dialog>

            <Dialog open={vm.isDeletePersonaDialogOpen} modalType="alert">
                <DialogSurface className={styles.deletePersonaDialog}>
                    <DialogBody>
                        <DialogTitle className={styles.deletePersonaTitle}>
                            Are you sure you want to delete this saved persona?
                        </DialogTitle>
                        <DialogActions className={styles.deletePersonaActions}>
                            <DialogTrigger action="close" disableButtonEnhancement>
                                <Button
                                    className={mergeClasses(styles.deletePersonaClose, styles.deletePersonaButton)}
                                    appearance="secondary"
                                    disabled={vm.isPersonaSaving}
                                    onClick={vm.closeDeletePersonaDialog}
                                >
                                    Cancel
                                </Button>
                            </DialogTrigger>
                            <DialogTrigger action="close" disableButtonEnhancement>
                                <Button
                                    className={mergeClasses(styles.deletePersonaDelete, styles.deletePersonaButton)}
                                    onClick={() => void vm.deletePersona()}
                                    disabled={vm.isPersonaSaving || !vm.personaPendingDelete}
                                >
                                    {vm.isPersonaSaving ? 'Deleting...' : 'Delete'}
                                </Button>
                            </DialogTrigger>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            </Dialog>
        </>
    );
};

export default PersonaTrayItem;
